export default class CliError extends Error {
  type: CliErrorType;
  constructor(
    message: string,
    { cause, type = 'error' }: { cause?: Error; type?: CliErrorType } = {},
  ) {
    super(message, { cause });
    this.type = type;
  }

  static from(e: unknown, type: CliErrorType = 'error'): CliError {
    if (e instanceof CliError) {
      return new CliError(e.message, { cause: e, type });
    } else if (e instanceof Error) {
      return new CliError(e.message, { cause: e, type });
    } else if (typeof e === 'string') {
      return new CliError(e, { type });
    } else {
      return new CliError(getErrorMessage(e), { type });
    }
  }
}

export type CliErrorType = 'build-error' | 'error';

export function toError<T = object, TRes = T & NodeJS.ErrnoException>(
  e: unknown,
  defaultMessage = 'Unknown error',
): TRes {
  if (e instanceof Error) {
    return e as TRes;
  }

  if (typeof e === 'string') {
    return new Error(e) as TRes;
  }

  if (e) {
    try {
      return new Error(JSON.stringify(e)) as TRes;
    } catch (serializeError) {
      // Can throw on circular/non-serializable objects
      return new Error(String(serializeError)) as TRes;
    }
  }

  return new Error(defaultMessage) as TRes;
}

export function getErrorMessage(
  error: unknown,
  fallbackMessage = 'Unexpected internal error',
): string {
  if (error instanceof CliError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim() !== '') {
    return error;
  }

  if (error && typeof error === 'object') {
    const message = extractMessage(error);
    if (message) {
      return message;
    }
  }

  return fallbackMessage;
}

function extractMessage(error: object): string | undefined {
  if ('message' in error && typeof error.message === 'string') {
    const message = error.message.trim();
    if (message !== '') {
      return message;
    }
  }

  return undefined;
}
